import { DataTable } from '@peritasai/ui-components';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-premium';
import type { AssetEntity } from '@peritasai/repository';
import { useTranslation } from 'react-i18next';
import { formatAssetType } from '../../utils/formatAssetType';
import { renderTableCell } from '../shared/renderTableCell';

const getColumns = (t: (key: string) => string): GridColDef<AssetEntity>[] => [
  {
    field: 'name',
    headerName: t('pages.entityManagement.trackedAssetsTable.columnName'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value, true),
  },
  {
    field: 'referenceNumber',
    headerName: t('pages.entityManagement.trackedAssetsTable.columnReferenceNumber'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'type',
    headerName: t('pages.entityManagement.trackedAssetsTable.columnType'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value === null ? null : `${formatAssetType(params.value)} ${params.row.trayType !== null ? `(${t(`pages.entityManagement.assetCatalogDrawer.trayTypeOptions.${params.row.trayType}`)})` : ''}`),
  },
  {
    field: 'status',
    headerName: t('pages.entityManagement.trackedAssetsTable.columnStatus'),
    flex: 1,
    renderCell: (params) => renderTableCell(params.value),
  },
  {
    field: 'manufacturer',
    headerName: t('pages.entityManagement.trackedAssetsTable.columnManufacturer'),
    flex: 1,
    headerClassName: 'last-column-header',
    renderCell: (params) => renderTableCell(params.value),
  },
];

interface TrackedAssetsTableProps {
  rows: AssetEntity[];
  rowCount: number;
  isLoading: boolean;
  hasSearch: boolean;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onRowClick: (row: AssetEntity) => void;
}

export const TrackedAssetsTable = ({
  rows,
  rowCount,
  isLoading,
  hasSearch,
  paginationModel,
  onPaginationModelChange,
  onRowClick,
}: TrackedAssetsTableProps) => {
  const { t } = useTranslation(['periverse']);
  const columns = getColumns(t);

  return (
    <DataTable
      rows={rows}
      columns={columns}
      getRowId={(row) => row.id}
      rowCount={rowCount}
      isLoading={isLoading}
      hasSearch={hasSearch}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      onRowClick={onRowClick}
      emptyMessage={t('pages.entityManagement.table.noRows', {
        entityName: t('pages.entityManagement.items.trackedAssets'),
      })}
      skeletonColumnCount={5}
    />
  );
};
